import { DateTime } from "luxon";
import { FC, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";

import { ActionsWrap, Td } from "@/components/Table";
import { SerpSearchResource } from "@/types/resources";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    &:last-child {
      cursor: initial;
    }
  }
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

interface Props {
  search: SerpSearchResource;
}

const SearchesTableRow: FC<Props> = ({ search }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleRowClick = (id: number) => {
    navigate(`/general/amazon-products-serp/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this search?"
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/amazon-searches/${search.id}`
      );

      await queryClient.invalidateQueries(["amazon_searches_list"], {
        exact: false,
      });

      alerts.success("Amazon Search was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };
  return (
    <Tr onClick={() => handleRowClick(search.id)}>
      <Td>{search.name}</Td>
      <Td>{DateTime.fromISO(search.created_at).toFormat("dd/LL/yyyy t")}</Td>
      <Td>{search.creator ?? "-"}</Td>
      <Td>{search.products_count ?? 0}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default SearchesTableRow;
